import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';
import apiClient from '../../services/api/apiClient';

function Step2ContentPurpose() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editAccountId = searchParams.get('edit');
  const isEditMode = !!editAccountId;

  const [selectedPurposes, setSelectedPurposes] = useState([]);
  const [loading, setLoading] = useState(isEditMode);

  // 編輯模式：載入帳號設定並預填
  useEffect(() => {
    if (isEditMode && editAccountId) {
      loadAccountSettings(editAccountId);
    }
  }, [isEditMode, editAccountId]);

  const loadAccountSettings = async (accountId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/user/social-accounts/account-settings/id/${accountId}`);

      if (response.data.success && response.data.data.settings) {
        const settings = response.data.data.settings;
        // 如果有 purposes，預填
        if (settings.purposes && Array.isArray(settings.purposes)) {
          setSelectedPurposes(settings.purposes);
        }
      }
    } catch (error) {
      console.error('載入帳號設定失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const purposes = [
    {
      id: 'engagement',
      title: '增加互動與參與度',
      description: '提高粉絲留言、分享、按讚等互動行為',
      icon: 'mdi:chat-processing-outline',
      color: 'blue'
    },
    {
      id: 'product_promotion',
      title: '推廣特定商品或服務',
      description: '宣傳產品特色、促進銷售轉換',
      icon: 'mdi:cart-outline',
      color: 'green'
    },
    {
      id: 'brand_awareness',
      title: '提升品牌知名度與曝光',
      description: '擴大品牌影響力、觸及更多潛在受眾',
      icon: 'mdi:bullhorn-outline',
      color: 'purple'
    },
    {
      id: 'community_building',
      title: '建立社群與忠誠度',
      description: '培養忠實粉絲、建立長期互信關係',
      icon: 'mdi:account-group-outline',
      color: 'pink'
    },
    {
      id: 'education',
      title: '教育與提供價值內容',
      description: '分享專業知識、提供實用資訊',
      icon: 'mdi:school-outline',
      color: 'orange'
    }
  ];

  const togglePurpose = (purposeId) => {
    setSelectedPurposes(prev => {
      if (prev.includes(purposeId)) {
        return prev.filter(id => id !== purposeId);
      } else {
        return [...prev, purposeId];
      }
    });
  };

  const handleNext = () => {
    if (selectedPurposes.length > 0) {
      localStorage.setItem('onboarding_purposes', JSON.stringify(selectedPurposes));

      // 編輯模式：帶上帳號 ID 到下一步
      if (isEditMode && editAccountId) {
        navigate(`/onboarding/step3?edit=${editAccountId}`);
      } else {
        navigate('/onboarding/step3');
      }
    }
  };

  const handleBack = () => {
    // 編輯模式：返回時帶上帳號 ID
    if (isEditMode && editAccountId) {
      navigate(`/onboarding/step1?edit=${editAccountId}`);
    } else {
      navigate('/onboarding/step1');
    }
  };

  const getColorClasses = (color, isSelected) => {
    const colors = {
      blue: {
        border: isSelected ? 'border-blue-500' : 'border-gray-200',
        bg: isSelected ? 'bg-blue-50' : '',
        iconBg: isSelected ? 'bg-blue-500' : 'bg-gray-100',
        iconText: isSelected ? 'text-white' : 'text-gray-600',
        checkColor: 'text-blue-500'
      },
      green: {
        border: isSelected ? 'border-green-500' : 'border-gray-200',
        bg: isSelected ? 'bg-green-50' : '',
        iconBg: isSelected ? 'bg-green-500' : 'bg-gray-100',
        iconText: isSelected ? 'text-white' : 'text-gray-600',
        checkColor: 'text-green-500'
      },
      purple: {
        border: isSelected ? 'border-purple-500' : 'border-gray-200',
        bg: isSelected ? 'bg-purple-50' : '',
        iconBg: isSelected ? 'bg-purple-500' : 'bg-gray-100',
        iconText: isSelected ? 'text-white' : 'text-gray-600',
        checkColor: 'text-purple-500'
      },
      pink: {
        border: isSelected ? 'border-pink-500' : 'border-gray-200',
        bg: isSelected ? 'bg-pink-50' : '',
        iconBg: isSelected ? 'bg-pink-500' : 'bg-gray-100',
        iconText: isSelected ? 'text-white' : 'text-gray-600',
        checkColor: 'text-pink-500'
      },
      orange: {
        border: isSelected ? 'border-orange-500' : 'border-gray-200',
        bg: isSelected ? 'bg-orange-50' : '',
        iconBg: isSelected ? 'bg-orange-500' : 'bg-gray-100',
        iconText: isSelected ? 'text-white' : 'text-gray-600',
        checkColor: 'text-orange-500'
      }
    };
    return colors[color];
  };

  const illustration = (
    <div className="w-full max-w-md mx-auto">
      <div className="relative">
        {/* Simple geometric illustration */}
        <div className="relative flex items-center justify-center">
          {/* Large circle */}
          <div className="w-64 h-64 rounded-full bg-white/60 backdrop-blur-sm shadow-lg flex items-center justify-center">
            {/* Inner circle with icon */}
            <div className="w-40 h-40 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <Icon icon="mdi:target" className="text-7xl text-white" />
            </div>
          </div>

          {/* Floating elements - purpose icons */}
          <div className="absolute -top-4 -right-4 w-20 h-20 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-400 shadow-lg flex items-center justify-center rotate-12">
            <Icon icon="mdi:cart-outline" className="text-3xl text-white" />
          </div>
          <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 shadow-lg flex items-center justify-center">
            <Icon icon="mdi:account-group-outline" className="text-2xl text-white" />
          </div>
          <div className="absolute top-1/2 -left-8 w-12 h-12 rounded-lg bg-gradient-to-br from-orange-400 to-yellow-400 shadow-lg flex items-center justify-center -rotate-12">
            <Icon icon="mdi:school-outline" className="text-xl text-white" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <OnboardingLayout
      currentStep={2}
      totalSteps={6}
      title="你的互動目的？"
      illustration={illustration}
      onBack={handleBack}
      progressColor="#60A5FA"
    >
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon icon="mdi:information-outline" className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-1">幫助 AI 理解你的內容目標</p>
            <p className="text-gray-600">
              根據你的目的，AI 會調整內容策略與語氣。可以複選多個目的。
            </p>
          </div>
        </div>
      </div>

      {/* Instruction Text */}
      <p className="text-sm text-gray-500 text-center mb-6">
        選擇 1 個或多個符合你經營目標的選項 <span className="text-blue-500 font-medium">（可複選）</span>
      </p>

      {/* Purpose Options */}
      <div className="grid grid-cols-1 gap-4">
        {purposes.map((purpose) => {
          const isSelected = selectedPurposes.includes(purpose.id);
          const colorClasses = getColorClasses(purpose.color, isSelected);

          return (
            <button
              key={purpose.id}
              onClick={() => togglePurpose(purpose.id)}
              className={`
                p-6 rounded-xl border-2 transition-all text-left
                ${colorClasses.border} ${colorClasses.bg}
                ${isSelected ? 'shadow-md' : 'hover:border-gray-300 hover:shadow-sm'}
              `}
            >
              <div className="flex items-start gap-4">
                <div className={`
                  w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                  ${colorClasses.iconBg}
                `}>
                  <Icon
                    icon={purpose.icon}
                    className={`text-2xl ${colorClasses.iconText}`}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {purpose.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {purpose.description}
                  </p>
                </div>
                {isSelected && (
                  <Icon
                    icon="mdi:check-circle"
                    className={`text-2xl flex-shrink-0 ${colorClasses.checkColor}`}
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Count */}
      {selectedPurposes.length > 0 && (
        <div className="mt-4 text-center">
          <span className="text-sm text-gray-600">
            已選擇 <span className="font-semibold text-blue-500">{selectedPurposes.length}</span> 個目的
          </span>
        </div>
      )}

      {/* Next Button */}
      <div className="flex justify-end mt-8">
        <button
          onClick={handleNext}
          disabled={selectedPurposes.length === 0}
          className={`
            px-8 py-3 font-medium rounded-xl transition-colors flex items-center gap-2
            ${selectedPurposes.length > 0
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          下一步
          <Icon icon="mdi:arrow-right" className="text-xl" />
        </button>
      </div>
    </OnboardingLayout>
  );
}

export default Step2ContentPurpose;
